import type { DateRangePreset } from './salesReportingConstants';
import { DATE_RANGE_PRESET_LABELS, DATE_RANGE_PRESETS } from './salesReportingConstants';

/**
 * Orden de match: rangos más cortos primero.
 * Si no, "hoy" caería en "últimos 7 días" / "este mes".
 */
const MATCHABLE_DATE_PRESETS = DATE_RANGE_PRESETS.filter(
  (p): p is Exclude<(typeof DATE_RANGE_PRESETS)[number], 'custom'> => p !== 'custom'
);

/** Rango personalizado (fechas locales). */
export type ReportsCustomRange = {
  from: Date;
  to: Date;
};

function startOfDayLocal(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDayLocal(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/** Lunes 00:00 local de la semana calendario que contiene `d` (ISO: lunes = inicio). */
export function startOfWeekMondayLocal(d: Date): Date {
  const x = startOfDayLocal(d);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}

function endOfWeekSundayLocal(d: Date): Date {
  const mon = startOfWeekMondayLocal(d);
  const end = new Date(mon);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

export type ReportsDateRange = {
  fromIso: string;
  toIso: string;
  dateForBandsIso: string;
};

export function getSalesDateRange(
  now: Date,
  preset: DateRangePreset,
  custom?: ReportsCustomRange | null
): ReportsDateRange {
  const todayStart = startOfDayLocal(now);
  const todayIso = todayStart.toISOString();
  const todayEnd = endOfDayLocal(now);
  const todayEndIso = todayEnd.toISOString();

  if (preset === 'custom') {
    if (!custom) {
      return { fromIso: todayIso, toIso: todayEndIso, dateForBandsIso: todayIso };
    }
    const from = startOfDayLocal(custom.from);
    const to = endOfDayLocal(custom.to);
    if (from.getTime() > to.getTime()) {
      return { fromIso: todayIso, toIso: todayEndIso, dateForBandsIso: todayIso };
    }
    return {
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
      dateForBandsIso: from.toISOString(),
    };
  }

  if (preset === 'yesterday') {
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const from = startOfDayLocal(yesterday);
    const to = endOfDayLocal(from);
    return {
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
      dateForBandsIso: from.toISOString(),
    };
  }

  if (preset === 'week') {
    const from = startOfWeekMondayLocal(now);
    const to = endOfWeekSundayLocal(now);
    return {
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
      dateForBandsIso: todayIso,
    };
  }

  if (preset === 'last_7_days' || preset === 'last_30_days') {
    // Rolling: N días corridos terminando hoy (incluye hoy).
    const days = preset === 'last_7_days' ? 7 : 30;
    const from = startOfDayLocal(now);
    from.setDate(from.getDate() - (days - 1));
    return {
      fromIso: from.toISOString(),
      toIso: todayEndIso,
      dateForBandsIso: todayIso,
    };
  }

  if (preset === 'this_month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return {
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
      dateForBandsIso: todayIso,
    };
  }

  if (preset === 'last_month') {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
    const to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    return {
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
      dateForBandsIso: todayIso,
    };
  }

  if (preset === 'last_12_months') {
    const to = endOfDayLocal(now);
    const from = new Date(now);
    from.setMonth(from.getMonth() - 12);
    const fromStart = startOfDayLocal(from);
    return {
      fromIso: fromStart.toISOString(),
      toIso: to.toISOString(),
      dateForBandsIso: todayIso,
    };
  }

  return {
    fromIso: todayIso,
    toIso: todayEndIso,
    dateForBandsIso: todayIso,
  };
}

/** Un solo día calendario local (franjas horarias). */
export function isSalesTimeBandsApplicable(range: ReportsDateRange): boolean {
  const a = new Date(range.fromIso);
  const b = new Date(range.toIso);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Período calendario anterior homólogo (para comparar ventas del período vs el anterior).
 */
export function getPreviousHomologousPeriod(
  now: Date,
  preset: DateRangePreset,
  current: ReportsDateRange,
  custom?: ReportsCustomRange | null
): ReportsDateRange | null {
  if (
    (preset === 'custom' && custom) ||
    preset === 'last_12_months' ||
    preset === 'last_7_days' ||
    preset === 'last_30_days'
  ) {
    // Ventana inmediatamente anterior del mismo largo.
    const curFromMs = new Date(current.fromIso).getTime();
    const curToMs = new Date(current.toIso).getTime();
    const span = curToMs - curFromMs;
    const prevToMs = curFromMs - 1;
    const prevFromMs = prevToMs - span;
    const prevFrom = new Date(prevFromMs);
    const prevTo = new Date(prevToMs);
    return {
      fromIso: prevFrom.toISOString(),
      toIso: prevTo.toISOString(),
      dateForBandsIso: prevFrom.toISOString(),
    };
  }

  if (preset === 'today') {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    return getSalesDateRange(y, 'yesterday');
  }

  if (preset === 'yesterday') {
    const y = new Date(now);
    y.setDate(y.getDate() - 2);
    const from = startOfDayLocal(y);
    const to = endOfDayLocal(from);
    return {
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
      dateForBandsIso: from.toISOString(),
    };
  }

  if (preset === 'week') {
    const thisMonday = startOfWeekMondayLocal(now);
    const prevMonday = new Date(thisMonday);
    prevMonday.setDate(prevMonday.getDate() - 7);
    const from = startOfDayLocal(prevMonday);
    const to = endOfWeekSundayLocal(prevMonday);
    return {
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
      dateForBandsIso: from.toISOString(),
    };
  }

  if (preset === 'this_month') {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
    const to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    return {
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
      dateForBandsIso: from.toISOString(),
    };
  }

  if (preset === 'last_month') {
    const from = new Date(now.getFullYear(), now.getMonth() - 2, 1, 0, 0, 0, 0);
    const to = new Date(now.getFullYear(), now.getMonth() - 1, 0, 23, 59, 59, 999);
    return {
      fromIso: from.toISOString(),
      toIso: to.toISOString(),
      dateForBandsIso: from.toISOString(),
    };
  }

  return null;
}

export function getSalesDateRangeLabel(preset: DateRangePreset, custom?: ReportsCustomRange | null): string {
  if (preset === 'custom' && custom) {
    const a = startOfDayLocal(custom.from).toLocaleDateString('es-AR');
    const b = endOfDayLocal(custom.to).toLocaleDateString('es-AR');
    return `${a} — ${b}`;
  }
  return DATE_RANGE_PRESET_LABELS[preset];
}

/** Fecha local YYYY-MM-DD. */
export function formatLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function ymdToStartIso(ymd: string): string {
  const parts = ymd.trim().split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now.toISOString();
  }
  const [y, m, d] = parts;
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

export function ymdToEndIso(ymd: string): string {
  const parts = ymd.trim().split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) {
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    return now.toISOString();
  }
  const [y, m, d] = parts;
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

export function getLocalYmdRangeFromPreset(preset: DateRangePreset): { fromYmd: string; toYmd: string } {
  if (preset === 'custom') {
    const ymd = formatLocalYmd(new Date());
    return { fromYmd: ymd, toYmd: ymd };
  }
  const { fromIso, toIso } = getSalesDateRange(new Date(), preset);
  return { fromYmd: formatLocalYmd(new Date(fromIso)), toYmd: formatLocalYmd(new Date(toIso)) };
}

/**
 * Identifica el preset que corresponde a un rango YMD aplicado.
 * Incluye this_month / last_month / etc. (no solo hoy/ayer/semana del historial POS).
 * Si no coincide ningún preset nombrado → `custom`.
 */
export function matchPosSalesHistoryDatePreset(fromYmd: string, toYmd: string): DateRangePreset | null {
  const from = fromYmd.trim();
  const to = toYmd.trim();
  if (!from || !to) return null;

  for (const preset of MATCHABLE_DATE_PRESETS) {
    const r = getLocalYmdRangeFromPreset(preset);
    if (r.fromYmd === from && r.toYmd === to) return preset;
  }
  return 'custom';
}
